/**
 * Transient feedback, in the prototype's `.crm-toast` shell.
 *
 * The shell is `app.css`'s — icon square, title, detail line, dismiss button,
 * a coloured left edge per tone, and the `toastIn`/`toastOut` keyframes. The
 * earlier version used the entry animation and never the exit one, so a toast
 * arrived gracefully and then blinked out of existence.
 *
 * Each toast owns its own lifetime, in its own component. That is the fix for
 * something subtler: the timers used to be created by one effect over the whole
 * array, keyed on the array itself, so every arriving toast cleared and
 * recreated the timers of the toasts already on screen — a burst of five
 * messages kept resetting the first one's five seconds, and the stack sat there
 * far longer than any of its members had asked for.
 *
 * What does not auto-dismiss: errors. An error that vanishes before it has been
 * read is an error the user will hit again, not knowing why.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** How long each tone stays, in ms. 0 means "until dismissed". */
const LIFETIME = { success: 4200, info: 5000, warning: 7000, danger: 0 };

const ICON = {
  success: 'bi-check-circle-fill',
  danger: 'bi-x-circle-fill',
  warning: 'bi-exclamation-triangle-fill',
  info: 'bi-info-circle-fill',
};

/** `tone` is the app's vocabulary; `app.css` names the classes differently. */
const TONE_CLASS = { success: 'success', danger: 'error', warning: 'warning', info: 'info' };

/** Matches `toastOut` in app.css. Kept here so the two cannot drift silently. */
const EXIT_MS = 220;

function Toast({ toast, onDismiss }) {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);

  const life = LIFETIME[toast.tone] ?? LIFETIME.info;

  const close = useCallback(() => {
    setLeaving(true);
    /* Unmount after the exit animation rather than on click, so the toast
       actually plays `toastOut`. Removing the row from the store immediately
       would take the element with it mid-animation. */
    setTimeout(() => onDismiss(toast.id), EXIT_MS);
  }, [onDismiss, toast.id]);

  /* An **absolute** deadline, not a remaining duration.
     
     This effect re-runs whenever `close` changes identity, and `close` depends
     on the `onDismiss` its parent passes — which is recreated on every render of
     the shell, i.e. every time any other toast appears or leaves. With a
     duration, each re-run called `setTimeout(close, life)` again and the toast's
     five seconds started over: two messages 2.5s apart both vanished together,
     6.3s after the first one arrived, because the second kept resetting the
     first. A deadline fixed at mount cannot be pushed back by a re-render.
     
     A pause moves the deadline out by exactly as long as the pause lasted, so
     hovering to read a message costs the reader nothing and gives them no less
     than the time they had left. */
  const deadline = useRef(Date.now() + life);
  const pausedAt = useRef(null);

  useEffect(() => {
    if (life === 0 || leaving) return undefined;
    if (paused) {
      pausedAt.current = Date.now();
      return undefined;
    }
    if (pausedAt.current !== null) {
      deadline.current += Date.now() - pausedAt.current;
      pausedAt.current = null;
    }
    const t = setTimeout(close, Math.max(0, deadline.current - Date.now()));
    return () => clearTimeout(t);
  }, [paused, leaving, life, close]);

  return (
    <div
      className={`crm-toast ${TONE_CLASS[toast.tone] ?? 'info'} ${leaving ? 'out' : ''}`}
      /* Errors interrupt; everything else waits its turn in the queue. A
         confirmation that a save worked is not worth cutting across whatever a
         screen reader is in the middle of saying. */
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="ti"><i className={`bi ${ICON[toast.tone] ?? ICON.info}`} aria-hidden="true" /></div>
      <div className="flex-grow-1 min-w-0">
        <div className="tt">
          {toast.message}
          {toast.count > 1 && <span className="tn" aria-label={`${toast.count} times`}>×{toast.count}</span>}
        </div>
        {toast.detail && <div className="tm">{toast.detail}</div>}
      </div>
      <button type="button" className="tx" onClick={close} aria-label="Dismiss">
        <i className="bi bi-x-lg" aria-hidden="true" />
      </button>

      {/* The countdown, so a toast that is about to go says so. Driven by the
          CSS animation and paused with it, which keeps the bar and the timer
          telling the same story without a second clock in JavaScript. */}
      {life > 0 && !leaving && (
        <span
          className="tp"
          style={{ animationDuration: `${life}ms`, animationPlayState: paused ? 'paused' : 'running' }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default function ToastStack({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;

  return (
    <div className="crm-toasts">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
